import React, { useEffect, useCallback } from 'react';
import { debug } from '@/utils/debug';
import { formatAccountId } from '@/utils/googleAds';
import styles from './PollingStates.module.css';
import modalStyles from '@/styles/ModalStyles.module.css';

interface PollingTimeoutProps {
  customerId: string;
  onManualCheck: () => void;
  onResumePolling: () => void;
  onGoHome: () => void;
}

export function PollingTimeout({ 
  customerId, 
  onManualCheck, 
  onResumePolling, 
  onGoHome 
}: PollingTimeoutProps) {
  debug.log('PollingTimeout: Rendering timeout state', { customerId });

  const handleManualCheck = () => {
    debug.log('PollingTimeout: Manual check requested');
    onManualCheck();
  };

  const handleResumePolling = () => {
    debug.log('PollingTimeout: Resume polling requested');
    onResumePolling();
  };

  const handleGoHome = () => {
    debug.log('PollingTimeout: Go home requested');
    onGoHome();
  };

  // Handle Escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onGoHome();
    }
  }, [onGoHome]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const modalTitleId = 'polling-timeout-title';

  return (
    <div
      className={modalStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.modalHeader}>
          <h3 id={modalTitleId} className={modalStyles.modalTitle}>Connection Timeout</h3>
          <button
            className={modalStyles.modalClose}
            onClick={onGoHome}
            aria-label="Close dialog"
          >
            &times;
          </button>
        </div>

        <div className={modalStyles.modalBody}>
          <div className={styles.container}>
            <div className={styles.header}>
              <div className={`${styles.icon} ${styles.timeoutIcon}`}>⏰</div>
            </div>

            <div className={styles.content}>
        <div className={styles.message}>
          <p className={styles.primaryText}>
            We've been checking for your Google Ads connection for several minutes.
          </p>
          <p className={styles.secondaryText}>
            <strong>Customer ID:</strong> {formatAccountId(customerId)}
          </p>
        </div>

        <div className={`${styles.infoSection} ${styles.timeoutSection}`}>
          <h4 className={styles.sectionTitle}>What might be happening?</h4>
          <ul className={styles.stepsList}>
            <li>The invitation email may still be on its way</li>
            <li>You might need to check your spam/junk folder</li>
            <li>The Google Ads console might require additional verification</li>
            <li>There could be a temporary delay with Google's systems</li>
          </ul>
        </div>

        <div className={`${styles.infoSection} ${styles.helpSection}`}>
          <h4 className={styles.sectionTitle}>Next steps:</h4>
          <ul className={styles.stepsList}>
            <li><strong>Check your email</strong> - Look for the Google Ads invitation</li>
            <li><strong>Try manual check</strong> - Click below to check immediately</li>
            <li><strong>Resume waiting</strong> - Continue automated checking</li>
            <li><strong>Complete later</strong> - Return when the email arrives</li>
          </ul>
        </div>

        <div className={styles.actions}>
          <button 
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleManualCheck}
          >
            Check Now
          </button>
          
          <button 
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={handleResumePolling}
          >
            Resume Waiting
          </button>
          
          <button 
            className={`${styles.btn} ${styles.btnTertiary}`}
            onClick={handleGoHome}
          >
            Complete Later
          </button>
        </div>

        <div className={`${styles.help} ${styles.timeoutHelp}`}>
          <p className={styles.helpText}>
            <strong>Still having issues?</strong> The Google Ads invitation process can sometimes take up to 15-20 minutes.
            You can safely return later and use "Check Now" when you've completed the email verification.
          </p>
        </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}